import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import BrokerLogo from '../components/BrokerLogo';

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '12px 14px', fontSize: '14px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '6px' };

const PLATFORMS = [
  { id: 'funded_apply', logo: 'funded_apply', label: 'Apply for ProfitPulse Funded', desc: 'Apply for a funded account — trade with up to $200,000 of firm capital', accent: '#00FF94', featured: true },
  { id: 'funded_link',  logo: 'funded_link',  label: 'Link Approved Funded Account', desc: 'Already approved? Enter your MT5 credentials to activate copying', accent: '#00FF94' },
  { id: 'tradelocker',  logo: 'tradelocker',  label: 'TradeLocker', desc: 'TradeLocker-based accounts (any prop firm)', accent: '#FF4949' },
  { id: 'metaapi',      logo: 'metaapi',      label: 'MetaTrader 5 / 4', desc: 'MT5 or MT4 — fully automated copy trading', accent: '#F28E00' },
  { id: 'tradovate',    logo: 'tradovate',    label: 'Tradovate', desc: 'Tradovate futures (any prop firm)', accent: '#7AFF00' },
  { id: 'matchtrader',  logo: 'matchtrader',  label: 'MatchTrader', desc: 'MatchTrader-based accounts (any prop firm)', accent: '#FFC107' },
  { id: 'rithmic',      logo: 'rithmic',      label: 'Rithmic (Futures)', desc: 'Rithmic FCM (any prop firm)', accent: '#9B2335' },
  { id: 'dxtrade',      logo: 'dxtrade',      label: 'DXtrade', desc: 'DXtrade futures (any prop firm)', accent: '#003D7A' },
  { id: 'volumetrica',  logo: 'volumetrica',  label: 'Volumetrica', desc: 'Volumetrica DeepChart (any prop firm)', accent: '#00C9D7' },
  { id: 'closeoption',  logo: 'closeoption',  label: 'CloseOption (Binary Options)', desc: 'Binary options trading — CALL/PUT on FX + crypto, multiple expiries', accent: '#FF6B35' },
];

export default function LinkBrokerPage() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});
  const [connected, setConnected] = useState(false);
  const [result, setResult] = useState(null);
  const [brokerSearch, setBrokerSearch] = useState('');
  const [brokerResults, setBrokerResults] = useState([]);
  const [isActiveStudent, setIsActiveStudent] = useState(false);
  const [propFirmRules, setPropFirmRules] = useState([]);

  React.useEffect(() => {
    api.getMe().then(u => setIsActiveStudent(u?.is_active_student || false)).catch(() => {});
    api.getPropFirmProfiles().then(r => setPropFirmRules(r || [])).catch(() => {});
  }, []);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // Reusable prop-firm disclosure block — shown on any broker-link form
  const PropFirmBlock = () => {
    const pickedRule = form.prop_firm_name ? propFirmRules.find(r => r.name.toLowerCase() === form.prop_firm_name.toLowerCase()) : null;
    return (
      <div style={{ marginBottom: 16, padding: 14, background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', color: 'var(--pp-text-2)' }}>
          <input data-testid="is-prop-firm-check" type="checkbox" checked={!!form.is_prop_firm} onChange={e => setField('is_prop_firm', e.target.checked)} />
          <span>This is a <strong>prop firm</strong> / funded-evaluation account</span>
        </label>
        {form.is_prop_firm && (
          <div style={{ marginTop: 12 }}>
            <label style={LABEL}>Which prop firm?</label>
            <select data-testid="prop-firm-select" style={{ ...INPUT, cursor: 'pointer' }} value={form.prop_firm_name || ''} onChange={e => setField('prop_firm_name', e.target.value)}>
              <option value="">— Select a prop firm —</option>
              {propFirmRules.map(r => <option key={r.rule_id} value={r.name}>{r.name}</option>)}
              <option value="__other__">Other (type below)</option>
            </select>
            {form.prop_firm_name === '__other__' && (
              <input style={{ ...INPUT, marginTop: 8 }} placeholder="Enter prop firm name" onChange={e => setField('prop_firm_name', e.target.value)} />
            )}
            <input style={{ ...INPUT, marginTop: 8 }} placeholder="Notes (optional) — program size, phase, etc." value={form.prop_firm_notes || ''} onChange={e => setField('prop_firm_notes', e.target.value)} />
            {pickedRule && (
              <div style={{ marginTop: 10, padding: 10, background: pickedRule.needs_playwright ? 'rgba(255,176,0,0.08)' : 'rgba(0,255,148,0.08)', border: '1px solid ' + (pickedRule.needs_playwright ? 'rgba(255,176,0,0.3)' : 'rgba(0,255,148,0.3)') }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: pickedRule.needs_playwright ? '#FFB000' : 'var(--pp-success)', letterSpacing: '0.1em' }}>
                  {pickedRule.needs_playwright ? '🛡 STRICT PROP FIRM DETECTED' : '✅ AUTO-COPY READY'}
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.6 }}>
                  {pickedRule.notes}
                  {pickedRule.needs_proxy && <> Admin will route your trades through a dedicated IP address to stay prop-firm-safe.</>}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      let data;
      if (platform === 'funded_apply') {
        const tierSizes = { funded_200k: 200000, funded_200k_eval: 200000, funded_400k: 400000, funded_400k_eval: 400000, funded_custom: parseInt(form.custom_amount) || 0 };
        const tier = form.tier || 'funded_200k';
        const size = tierSizes[tier];
        if (tier === 'funded_custom') {
          if (!size) { toast.error('Please enter your desired custom amount.'); setLoading(false); return; }
          await api.applyFundedAccount({ desired_size: size, message: `CUSTOM REQUEST: $${size.toLocaleString()} — ${form.message || ''}`, tier });
          toast.success('Custom request submitted! Our team will reach out within 24 hours.');
          navigate('/funded-accounts');
          return;
        }
        data = await api.applyFundedAccount({ desired_size: size, message: form.message || '', tier });
        toast.success('Application submitted! Complete payment to activate your funded account.');
        navigate(`/checkout?plan=${tier}&type=funded`);
        return;
      } else if (platform === 'funded_link') {
        // User selects which funded account to link — for now navigate to funded accounts page
        navigate('/funded-accounts');
        return;
      } else if (platform === 'tradelocker') {
        data = await api.connectTradeLocker({ email: form.email, password: form.password, server: form.server, environment: form.environment || 'https://live.tradelocker.com', account_label: form.account_label });
      } else if (platform === 'metaapi') {
        data = await api.connectMetaAPISlaveAccount({ login: form.login, password: form.password, server: form.server, platform: form.mt_platform || 'mt5', account_label: form.account_label, is_prop_firm: !!form.is_prop_firm, prop_firm_name: form.prop_firm_name || '', prop_firm_notes: form.prop_firm_notes || '' });
      } else if (platform === 'tradovate') {
        data = await api.connectTradovatePW({ username: form.username, password: form.password, environment: form.environment || 'demo', account_label: form.account_label });
      } else if (platform === 'matchtrader') {
        data = await api.connectMatchTrader({ server_url: form.server_url, email: form.email, password: form.password, broker_name: form.broker_name || 'MatchTrader Broker', account_label: form.account_label, environment: 'live' });
      } else if (platform === 'mt5') {
        data = await api.connectMT5({ account_number: form.account_number, server: form.server, broker_name: form.broker_name, account_label: form.account_label });
      } else if (platform === 'rithmic') {
        data = await api.connectRithmic({ username: form.username, password: form.password, system_name: form.system_name || 'Rithmic Paper Trading', account_label: form.account_label });
      } else if (platform === 'dxtrade') {
        data = await api.connectDXtrade({ username: form.username, password: form.password, server_url: form.server_url, account_label: form.account_label });
      } else if (platform === 'volumetrica') {
        data = await api.volumetricaConnect({
          email:          form.email,
          password:       form.password,
          prop_firm:      form.prop_firm_name || form.prop_firm || 'Aqua Futures',
          portal_url:     form.portal_url || 'https://volumetrica.aquafutures.io',
          account_number: form.account_number || null,
          account_label:  form.account_label,
          is_prop_firm:   !!form.is_prop_firm,
          prop_firm_notes: form.prop_firm_notes || '',
        });
      } else if (platform === 'closeoption') {
        data = await api.closeoptionConnect({
          email:               form.email,
          password:            form.password,
          portal_url:          form.portal_url || 'https://www.closeoption.com',
          account_label:       form.account_label,
          account_type:        form.account_type || 'real',
          default_expiry_secs: parseInt(form.default_expiry_secs || 60),
          default_stake:       parseFloat(form.default_stake || 5),
          is_prop_firm:        !!form.is_prop_firm,
          prop_firm:           form.prop_firm_name || form.prop_firm || '',
          prop_firm_notes:     form.prop_firm_notes || '',
        });
      }
      setResult(data);
      setConnected(true);
      toast.success('Account connected successfully!');
    } catch (e) {
      let msg = e.message;
      try { const d = JSON.parse(msg); msg = d.error || d.detail || msg; } catch {}
      toast.error(msg || 'Connection failed');
    }
    setLoading(false);
  };

  const handleMT5BrokerSearch = async (q) => {
    setBrokerSearch(q);
    if (q.length >= 2) {
      const results = await api.searchMT5Brokers(q).catch(() => []);
      setBrokerResults(results);
    } else {
      setBrokerResults([]);
    }
  };

  if (connected && result) {
    return (
      <div className="page-container">
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
          <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Account Connected</h1>
        </div>
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>Connected Successfully!</h2>
          <p style={{ color: 'var(--pp-text-2)', fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>{result.message}</p>
          <button onClick={() => navigate('/profile')} className="btn-primary" style={{ width: '100%', height: 52 }}>VIEW PROFILE</button>
        </div>
      </div>
    );
  }

  if (platform) {
    const plat = PLATFORMS.find(p => p.id === platform);
    return (
      <div className="page-container">
        <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
          <button onClick={() => setPlatform(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Connect {plat?.label}</h1>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: 'var(--pp-text-2)', marginBottom: 24 }}>{plat?.desc}</p>

          {platform === 'funded_apply' && (
            <>
              <div style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.2)', padding: 14, marginBottom: 20, fontSize: 13, color: 'var(--pp-text-2)', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--pp-success)' }}>ProfitPulse Funded Program</strong><br />
                Trade with firm capital on Chimara Ltd. Once approved, your account will be set up and you'll receive MT5 credentials to activate copy trading automatically.
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={LABEL}>Funded Account Tier</label>
                <select data-testid="funded-tier-select" style={{ ...INPUT, cursor: 'pointer' }} value={form.tier || 'funded_200k'} onChange={e => setField('tier', e.target.value)}>
                  <option value="funded_200k">$200,000 — Instant — $6,600</option>
                  <option value="funded_200k_eval">$200,000 — 10-Day Approval — $5,000</option>
                  <option value="funded_400k">$400,000 — Instant — $12,500</option>
                  <option value="funded_400k_eval">$400,000 — 10-Day Approval — $6,600</option>
                  <option value="funded_custom">Custom Amount (Contact Sales)</option>
                </select>
                {(form.tier === 'funded_200k_eval' || form.tier === 'funded_400k_eval') && (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: '#FFB000', lineHeight: 1.6 }}>
                    ⚠ This account may take up to <strong>10 business days</strong> to be approved and activated.
                  </p>
                )}
                {form.tier === 'funded_custom' && (
                  <div style={{ marginTop: 10 }}>
                    <label style={LABEL}>Desired Amount (USD)</label>
                    <input style={INPUT} type="number" placeholder="e.g. 500000" onChange={e => setField('custom_amount', e.target.value)} />
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                      Our team will review your request and respond with pricing within 24 hours.
                    </p>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={LABEL}>Trading Experience</label>
                <select style={{ ...INPUT, cursor: 'pointer' }} onChange={e => setField('experience', e.target.value)}>
                  <option value="">Select...</option>
                  {['0-3 months','3-6 months','6-12 months','1-2 years','2-3 years','3-5 years','5-6 years','6+ years'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <p style={{ margin: '16px 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--pp-text-3)' }}>BILLING ADDRESS</p>
              <div style={{ marginBottom: 12 }}><label style={LABEL}>Street Address</label><input style={INPUT} placeholder="123 Main St" onChange={e => setField('billing_street', e.target.value)} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><label style={LABEL}>City</label><input style={INPUT} placeholder="City" onChange={e => setField('billing_city', e.target.value)} /></div>
                <div><label style={LABEL}>State/Province</label><input style={INPUT} placeholder="State" onChange={e => setField('billing_state', e.target.value)} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div><label style={LABEL}>ZIP/Postal Code</label><input style={INPUT} placeholder="12345" onChange={e => setField('billing_zip', e.target.value)} /></div>
                <div><label style={LABEL}>Country</label><input style={INPUT} placeholder="United States" onChange={e => setField('billing_country', e.target.value)} /></div>
              </div>

              <p style={{ margin: '16px 0 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--pp-text-3)' }}>CRYPTO PAYOUT ADDRESS</p>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Network</label>
                <select style={{ ...INPUT, cursor: 'pointer' }} onChange={e => setField('crypto_network', e.target.value)}>
                  <option value="">Select network...</option>
                  {['Bitcoin','Ethereum','USDT-TRC20','USDT-ERC20','Solana'].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Wallet Address</label><input style={{ ...INPUT, fontFamily: 'monospace', fontSize: 12 }} placeholder="Paste wallet address..." onChange={e => setField('crypto_wallet', e.target.value)} /></div>
            </>
          )}

          {platform === 'funded_link' && (
            <div style={{ background: 'var(--pp-overlay)', padding: 16, textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'var(--pp-text-2)', marginBottom: 16 }}>To link your approved funded account, go to your Funded Accounts page and click <strong>Link MT5</strong> on the relevant account.</p>
              <button onClick={() => navigate('/funded-accounts')} className="btn-primary" style={{ width: '100%', height: 48 }}>GO TO FUNDED ACCOUNTS</button>
            </div>
          )}

          {platform === 'tradelocker' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Email</label><input style={INPUT} type="email" placeholder="your@email.com" onChange={e => setField('email', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="TradeLocker password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Server Name</label><input style={INPUT} placeholder="e.g. GATESFX" onChange={e => setField('server', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}>
                <label style={LABEL}>Environment</label>
                <select style={{ ...INPUT, cursor: 'pointer' }} onChange={e => setField('environment', e.target.value)}>
                  <option value="https://live.tradelocker.com">Live</option>
                  <option value="https://demo.tradelocker.com">Demo</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'metaapi' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>MT5/MT4 Login Number</label><input style={INPUT} placeholder="e.g. 1571813" onChange={e => setField('login', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="MT5 password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 8 }}><label style={LABEL}>Broker Server</label>
                <input style={INPUT} placeholder="Search brokers..." value={brokerSearch} onChange={e => { handleMT5BrokerSearch(e.target.value); setField('server', e.target.value); }} />
                {brokerResults.length > 0 && (
                  <div style={{ background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', maxHeight: 200, overflowY: 'auto' }}>
                    {brokerResults.flatMap(b => b.servers).map(s => (
                      <button key={s} onClick={() => { setField('server', s); setBrokerSearch(s); setBrokerResults([]); }} style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'none', border: 'none', color: 'var(--pp-text)', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid var(--pp-border)', fontFamily: 'monospace', fontSize: 13 }}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={LABEL}>Platform</label>
                <select style={{ ...INPUT, cursor: 'pointer' }} onChange={e => setField('mt_platform', e.target.value)}>
                  <option value="mt5">MetaTrader 5</option>
                  <option value="mt4">MetaTrader 4</option>
                </select>
              </div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
              <PropFirmBlock />
            </>
          )}

          {platform === 'tradovate' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Username</label><input style={INPUT} placeholder="Tradovate username" onChange={e => setField('username', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="Tradovate password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Environment</label><select style={{ ...INPUT, cursor: 'pointer' }} onChange={e => setField('environment', e.target.value)}><option value="demo">Demo</option><option value="live">Live</option></select></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'matchtrader' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Broker API URL</label><input style={INPUT} placeholder="https://broker.matchtrader.com" onChange={e => setField('server_url', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Broker Name</label><input style={INPUT} placeholder="e.g. FTMO, The5ers" onChange={e => setField('broker_name', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Email</label><input style={INPUT} type="email" placeholder="your@email.com" onChange={e => setField('email', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="Account password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'mt5' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Account Number</label><input style={INPUT} placeholder="e.g. 123456" onChange={e => setField('account_number', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Broker Name</label><input style={INPUT} placeholder="e.g. IC Markets" onChange={e => setField('broker_name', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Server Name</label><input style={INPUT} placeholder="e.g. ICMarkets-Live" onChange={e => setField('server', e.target.value)} /></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'rithmic' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Username</label><input style={INPUT} placeholder="Rithmic username" onChange={e => setField('username', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="Rithmic password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>System Name</label><input style={{ ...INPUT, background: 'var(--pp-surface)' }} defaultValue="Rithmic Paper Trading" onChange={e => setField('system_name', e.target.value)} /></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'dxtrade' && (
            <>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Username</label><input style={INPUT} placeholder="DXtrade username" onChange={e => setField('username', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="DXtrade password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Server URL</label><input style={INPUT} placeholder="https://trade.platform.com" onChange={e => setField('server_url', e.target.value)} /></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'volumetrica' && (
            <>
              <div style={{ marginBottom: 12, padding: 10, border: '1px solid rgba(0,201,215,0.35)', background: 'rgba(0,201,215,0.06)', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>
                <strong style={{ color: '#00C9D7' }}>Fully automated.</strong> When a copy signal fires we place the order directly on the Volumetrica DeepChart platform — no manual confirm step on your end.
              </div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Portal URL</label><input style={INPUT} placeholder="https://volumetrica.aquafutures.io" defaultValue="https://volumetrica.aquafutures.io" onChange={e => setField('portal_url', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Login Email</label><input style={INPUT} placeholder="Your Volumetrica login email" onChange={e => setField('email', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="Volumetrica password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Account # (optional)</label><input style={INPUT} placeholder="e.g. 12345" onChange={e => setField('account_number', e.target.value)} /></div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {platform === 'closeoption' && (
            <>
              <div style={{ marginBottom: 12, padding: 10, border: '1px solid rgba(255,107,53,0.35)', background: 'rgba(255,107,53,0.06)', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>
                <strong style={{ color: '#FF6B35' }}>Binary options · Fully automated.</strong> Copy signals fire directly on CloseOption. <strong>BUY → CALL, SELL → PUT.</strong> Stops/targets do not apply — binary trades settle on the expiry timer.
              </div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Portal URL</label><input style={INPUT} defaultValue="https://www.closeoption.com" onChange={e => setField('portal_url', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Login Email</label><input style={INPUT} placeholder="Your CloseOption email" onChange={e => setField('email', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Password</label><input style={INPUT} type="password" placeholder="CloseOption password" onChange={e => setField('password', e.target.value)} /></div>
              <div style={{ marginBottom: 16 }}><label style={LABEL}>Account Type</label>
                <select style={INPUT} defaultValue="real" onChange={e => setField('account_type', e.target.value)}>
                  <option value="real">Real ($5 min deposit)</option>
                  <option value="demo">Demo</option>
                  <option value="contest">Contest</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div><label style={LABEL}>Default Expiry</label>
                  <select style={INPUT} defaultValue="60" onChange={e => setField('default_expiry_secs', e.target.value)}>
                    <option value="30">30 seconds</option>
                    <option value="60">1 minute</option>
                    <option value="180">3 minutes</option>
                    <option value="300">5 minutes</option>
                    <option value="900">15 minutes</option>
                    <option value="1800">30 minutes</option>
                    <option value="3600">1 hour</option>
                  </select>
                </div>
                <div><label style={LABEL}>Default Stake ($)</label>
                  <input style={INPUT} type="number" min="1" max="1000" defaultValue="5" onChange={e => setField('default_stake', e.target.value)} />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}><label style={LABEL}>Account Label (optional)</label><input style={INPUT} placeholder="" onChange={e => setField('account_label', e.target.value)} /></div>
            </>
          )}

          {(platform === 'tradelocker' || platform === 'matchtrader' || platform === 'mt5' || platform === 'tradovate' || platform === 'rithmic' || platform === 'dxtrade' || platform === 'volumetrica' || platform === 'closeoption') && <PropFirmBlock />}

          <button onClick={handleConnect} disabled={loading} className="btn-primary" style={{ width: '100%', height: 56 }}>
            {loading ? 'CONNECTING...' : `CONNECT ${plat?.label?.toUpperCase()}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>LINK BROKER</h1>
      </div>
      <div style={{ padding: 20 }}>
        <p style={{ fontSize: 13, color: 'var(--pp-text-2)', marginBottom: 24 }}>Select your broker platform to connect for automated copy trading.</p>
        {PLATFORMS.filter(p => isActiveStudent || (!p.id.startsWith('funded'))).map(p => {
          return (
            <button key={p.id} data-testid={`broker-card-${p.id}`} onClick={() => { setPlatform(p.id); setForm({}); setBrokerSearch(''); setBrokerResults([]); }} style={{ width: '100%', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, textAlign: 'left', cursor: 'pointer', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
              <BrokerLogo platform={p.logo} size={48} />
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{p.label}</p>
                <p style={{ margin: 0, fontSize: 12, color: 'var(--pp-text-3)', marginTop: 3 }}>{p.desc}</p>
              </div>
              <ArrowLeft size={16} color="var(--pp-text-3)" style={{ transform: 'rotate(180deg)' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
